import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    products: [],
    cart: [],
    wishlist: [],
    checkout: {
      personalInfo: {},
      shippingInfo: {},
      paymentInfo: {}
    }
  },
  getters: {
    allProducts: state => state.products,
    menProducts: state => state.products.filter(product => product.category === 'men'),
    womenProducts: state => state.products.filter(product => product.category === 'women'),
    cartItems: state => state.cart,
    cartItemCount: state => state.cart.length,
    cartTotalPrice: state => {
      return state.cart.reduce((total, item) => {
        return total + (item.price * item.quantity);
      }, 0);
    },
    wishlistItems: state => state.wishlist,
    wishlistItemCount: state => state.wishlist.length,
    isInWishlist: state => productId => {
      return state.wishlist.some(item => item.id === productId);
    },
    checkoutInfo: state => state.checkout
  },
  mutations: {
    SET_PRODUCTS(state, products) {
      state.products = products;
    },
    ADD_TO_WISHLIST(state, product) {
      if (!state.wishlist.some(item => item.id === product.id)) {
        state.wishlist.push(product);
      }
    },
    REMOVE_FROM_WISHLIST(state, productId) {
      state.wishlist = state.wishlist.filter(item => item.id !== productId);
    },
    ADD_TO_CART(state, {product, quantity}) {
      let productInCart = state.cart.find(item => item.id === product.id);

      if (productInCart) {
        productInCart.quantity += quantity;
        return;
      }

      state.cart.push({
        ...product,
        quantity
      });
    },
    REMOVE_FROM_CART(state, productId) {
      state.cart = state.cart.filter(item => item.id !== productId);
    },
    UPDATE_CART_ITEM_QUANTITY(state, {productId, quantity}) {
      let item = state.cart.find(product => product.id === productId);
      if (item) {
        item.quantity = quantity;
      }
    },
    CLEAR_CART(state) {
      state.cart = [];
    },
    SET_PERSONAL_INFO(state, info) {
      state.checkout.personalInfo = info;
    },
    SET_SHIPPING_INFO(state, info) {
      state.checkout.shippingInfo = info;
    },
    SET_PAYMENT_INFO(state, info) {
      state.checkout.paymentInfo = info;
    }
  },
  actions: {
    addToWishlist({ commit }, product) {
      commit('ADD_TO_WISHLIST', product);
    },
    removeFromWishlist({ commit }, productId) {
      commit('REMOVE_FROM_WISHLIST', productId);
    },
    fetchProducts({ commit }) {
      // In a real app, this would be an API call. For now, we'll use hardcoded data.
      const products = [
        // Men's Products
        {
          id: 1,
          name: "Men's White T-Shirt",
          description: "Classic white t-shirt for men, made of 100% cotton",
          price: 1999,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/513fraGQXWL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['white'],
          featured: true
        },
        {
          id: 2,
          name: "Men's Blue Polo Shirt",
          description: "Stylish blue polo shirt for men, perfect for casual wear",
          price: 2499,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/71zkwoxdMpL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['blue'],
          featured: false
        },
        {
          id: 3,
          name: "Men's Black Jeans",
          description: "Comfortable black jeans for men, slim fit",
          price: 3999,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/516qsgg8PvL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['30', '32', '34', '36'],
          colors: ['black'],
          featured: true
        },
        {
          id: 4,
          name: "Men's Green Button-Down Shirt",
          description: "Elegant green button-down shirt for men",
          price: 2999,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/61NNdlWCy7L._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['green'],
          featured: false
        },
        {
          id: 5,
          name: "Men's Beige Chinos",
          description: "Classic beige chinos for men, regular fit",
          price: 3499,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/71X4a5+m+XL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['30', '32', '34', '36'],
          colors: ['beige'],
          featured: true
        },
        {
          id: 6,
          name: "Men's Brown Cardigan",
          description: "Warm brown cardigan for men, perfect for cold weather",
          price: 4999,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/91Si2NXoGZL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['brown'],
          featured: false
        },
        {
          id: 7,
          name: "Men's Gray Sweater",
          description: "Soft gray sweater for men, lightweight and comfortable",
          price: 4499,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/71wqlPJiimL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['gray'],
          featured: true
        },
        {
          id: 8,
          name: "Men's Blue Denim Jacket",
          description: "Classic blue denim jacket for men",
          price: 5999,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/71DoZhiZ0WS._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['blue'],
          featured: false
        },
        {
          id: 9,
          name: "Men's Black Dress Pants",
          description: "Elegant black dress pants for men, slim fit",
          price: 4499,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/71Tn25pVX+L._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['30', '32', '34', '36'],
          colors: ['black'],
          featured: true
        },
        {
          id: 10,
          name: "Men's Red Hoodie",
          description: "Comfortable red hoodie for men, with front pocket",
          price: 3999,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/81RaWtv4Q6L._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['red'],
          featured: false
        },
        {
          id: 11,
          name: "Men's Blue Shorts",
          description: "Comfortable blue shorts for men, perfect for summer",
          price: 2999,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/71o81PkqNpL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['30', '32', '34', '36'],
          colors: ['blue'],
          featured: true
        },
        {
          id: 12,
          name: "Men's Striped T-Shirt",
          description: "Stylish striped t-shirt for men, casual look",
          price: 2499,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/61htdROvI9L._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['blue', 'white'],
          featured: false
        },
        {
          id: 13,
          name: "Men's Brown Leather Jacket",
          description: "Elegant brown leather jacket for men",
          price: 12999,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/71ypQewD8jL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['brown'],
          featured: true
        },
        {
          id: 14,
          name: "Men's White Dress Shirt",
          description: "Classic white dress shirt for men, formal wear",
          price: 3999,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/51rkKPruYvL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['white'],
          featured: false
        },
        {
          id: 15,
          name: "Men's Navy Blue Blazer",
          description: "Elegant navy blue blazer for men, perfect for formal occasions",
          price: 9999,
          category: 'men',
          images: 'https://m.media-amazon.com/images/I/71LUJgXAGxL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['navy blue'],
          featured: true
        },
        // Women's Products
        {
          id: 16,
          name: "Women's Floral Dress",
          description: "Beautiful floral dress for women, perfect for summer",
          price: 4999,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/71CwKwMLckL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['multicolor'],
          featured: true
        },
        {
          id: 17,
          name: "Women's Black Jeans",
          description: "Comfortable black jeans for women, skinny fit",
          price: 3999,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/416epGtgz+L._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['26', '28', '30', '32'],
          colors: ['black'],
          featured: false
        },
        {
          id: 18,
          name: "Women's White Blouse",
          description: "Elegant white blouse for women, perfect for any occasion",
          price: 3499,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/81HrTzSgXzL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['white'],
          featured: true
        },
        {
          id: 19,
          name: "Women's Pink Sweater",
          description: "Soft pink sweater for women, warm and comfortable",
          price: 4499,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/71hAhwcIfHL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['pink'],
          featured: false
        },
        {
          id: 20,
          name: "Women's Blue Denim Skirt",
          description: "Stylish blue denim skirt for women",
          price: 2999,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/710KwIA48jL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['blue'],
          featured: true
        },
        {
          id: 21,
          name: "Women's Red Crop Top",
          description: "Trendy red crop top for women",
          price: 1999,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/71dW6jGTiRL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['red'],
          featured: false
        },
        {
          id: 22,
          name: "Women's Green Maxi Dress",
          description: "Elegant green maxi dress for women",
          price: 5999,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/61ZkK1ZYUvL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['green'],
          featured: true
        },
        {
          id: 23,
          name: "Women's Blue Blazer",
          description: "Stylish blue blazer for women, perfect for formal occasions",
          price: 8999,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/61kj86+BNYL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['blue'],
          featured: false
        },
        {
          id: 24,
          name: "Women's Black Leggings",
          description: "Comfortable black leggings for women, high waist",
          price: 2499,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/61IqZD8hYDL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['black'],
          featured: true
        },
        {
          id: 25,
          name: "Women's Yellow Sundress",
          description: "Beautiful yellow sundress for women, perfect for summer",
          price: 3999,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/813n+Ej9eIL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['yellow'],
          featured: false
        },
        {
          id: 26,
          name: "Women's White T-Shirt",
          description: "Classic white t-shirt for women, made of soft cotton",
          price: 1999,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/61LunrukImL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['white'],
          featured: true
        },
        {
          id: 27,
          name: "Women's Leopard Print Skirt",
          description: "Trendy leopard print skirt for women",
          price: 3499,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/71Qv9Jr3iLL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['multicolor'],
          featured: false
        },
        {
          id: 28,
          name: "Women's Purple Cardigan",
          description: "Cozy purple cardigan for women, perfect for layering",
          price: 4999,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/71NXIQSt9CL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['purple'],
          featured: true
        },
        {
          id: 29,
          name: "Women's Blue Jeans Shorts",
          description: "Comfortable blue jeans shorts for women",
          price: 2999,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/71qLaGQabuL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['26', '28', '30', '32'],
          colors: ['blue'],
          featured: false
        },
        {
          id: 30,
          name: "Women's Black Dress",
          description: "Elegant black dress for women, perfect for special occasions",
          price: 6999,
          category: 'women',
          images: 'https://m.media-amazon.com/images/I/61YKuWHaTuL._AC_UL640_FMwebp_QL65_.jpg',
          sizes: ['S', 'M', 'L', 'XL'],
          colors: ['black'],
          featured: true
        }
      ];

      commit('SET_PRODUCTS', products);
    },
    addToCart({ commit }, { product, quantity }) {
      commit('ADD_TO_CART', { product, quantity });
    },
    removeFromCart({ commit }, productId) {
      commit('REMOVE_FROM_CART', productId);
    },
    updateCartItemQuantity({ commit }, { productId, quantity }) {
      commit('UPDATE_CART_ITEM_QUANTITY', { productId, quantity });
    },
    clearCart({ commit }) {
      commit('CLEAR_CART');
    },
    savePersonalInfo({ commit }, info) {
      commit('SET_PERSONAL_INFO', info);
    },
    saveShippingInfo({ commit }, info) {
      commit('SET_SHIPPING_INFO', info);
    },
    savePaymentInfo({ commit }, info) {
      commit('SET_PAYMENT_INFO', info);
    }
  },
  modules: {
  }
})
